// Shared state of the AI search index (what the Study Companion can find), so every surface —
// the notebook menu, the chat widget, a future manager page — shows the same coverage and the
// same progress while indexing runs.
import { useSyncExternalStore } from 'react';
import { getIndexQueue, indexBatch, IndexQueueItem } from './geminiService';

export interface NotebookCoverage { total: number; indexed: number }

export interface SearchIndexState {
    /** Last scan of the whole account; null until the first scan finishes. */
    coverage: Record<string, NotebookCoverage> | null;
    totalNotes: number;
    needsIndex: number;
    scanning: boolean;
    /** Indexing in progress: notes done / notes to do in this run, and the notebook it is limited to. */
    running: { current: number; total: number; notebookId: string | null } | null;
    error: boolean;
}

// Stay under the 540s function timeout even when the embedding API backs off.
const BATCH_SIZE = 20;

let state: SearchIndexState = { coverage: null, totalNotes: 0, needsIndex: 0, scanning: false, running: null, error: false };
let pending: IndexQueueItem[] = [];
const listeners = new Set<() => void>();

function set(patch: Partial<SearchIndexState>) {
    state = { ...state, ...patch };
    listeners.forEach(l => l());
}

/** Rescan which notes are missing from (or stale in) the index. */
export async function refreshSearchIndex(): Promise<void> {
    if (state.scanning || state.running) return;
    set({ scanning: true, error: false });
    try {
        const queue = await getIndexQueue();
        pending = queue.items;
        set({ coverage: queue.perNotebook ?? {}, totalNotes: queue.totalNotes, needsIndex: queue.needsIndex, scanning: false });
    } catch {
        set({ scanning: false, error: true });
    }
}

/**
 * Index every note that is missing or has changed — for one notebook, or the whole account.
 * Safe to run again at any time: notes already indexed are skipped, so it continues where an
 * interrupted run stopped. Resolves with the number of notes newly indexed.
 */
export async function runSearchIndex(notebookId: string | null = null): Promise<{ indexed: number; totalNotes: number }> {
    if (state.running) return { indexed: 0, totalNotes: state.totalNotes };
    await refreshSearchIndex();
    if (state.error) throw new Error('scan failed');

    const items = pending.filter(i => !notebookId || i.notebookId === notebookId);
    if (!items.length) return { indexed: 0, totalNotes: state.totalNotes };

    let done = 0;
    let indexed = 0;
    set({ running: { current: 0, total: items.length, notebookId } });
    try {
        for (let i = 0; i < items.length; i += BATCH_SIZE) {
            const batch = items.slice(i, i + BATCH_SIZE);
            const result = await indexBatch(batch);
            indexed += result.indexed;
            done += result.indexed + result.skipped; // failed notes stay in the queue for the next run
            // Keep the per-notebook numbers moving while the run is in progress.
            const coverage = { ...(state.coverage ?? {}) };
            const ok = result.indexed + result.skipped;
            if (ok === batch.length) {
                for (const item of batch) {
                    const c = coverage[item.notebookId];
                    if (c) coverage[item.notebookId] = { ...c, indexed: Math.min(c.total, c.indexed + 1) };
                }
            }
            set({ coverage, running: { current: done, total: items.length, notebookId } });
        }
    } catch (e) {
        set({ running: null, error: true });
        throw e;
    }
    set({ running: null });
    await refreshSearchIndex(); // exact numbers, including any notes that failed
    return { indexed, totalNotes: state.totalNotes };
}

export function useSearchIndex(): SearchIndexState {
    return useSyncExternalStore(
        cb => { listeners.add(cb); return () => listeners.delete(cb); },
        () => state,
    );
}
